import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { UrlCheckerForm } from "./UrlCheckerForm";
import { UrlCheckerResult, CheckResult } from "./UrlCheckerResult";
import { UrlCheckerSkeleton } from "./UrlCheckerSkeleton";
import { CacheList } from "./CacheList";
import { useUrlCache } from "@/hooks/useUrlCache";
import { toast } from "sonner";

const CLOUDFLARE_WORKER_URL = "https://api.transparencybadge.com";

export function UrlChecker() {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasAutoChecked = useRef(false);
  
  const {
    cachedUrls,
    getCachedResult,
    cacheResult,
    clearCacheEntry,
    clearAllCache,
    getTimeRemaining,
  } = useUrlCache();

  const initialUrl = searchParams.get("site") || "";

  const handleCheck = async (url: string) => {
    setError(null);
    setResult(null);

    // Check cache first
    const cachedData = getCachedResult(url);
    if (cachedData) {
      setResult(cachedData);
      toast.success("Loaded from cache", {
        description: `Result cached for ${cachedData.domain}`,
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${CLOUDFLARE_WORKER_URL}/check-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        throw new Error("Failed to check website. Please try again.");
      }

      const data = await response.json();

      if (data?.error) {
        throw new Error(data.error);
      }

      const resultData = data as CheckResult;
      setResult(resultData);
      
      // Cache the result
      cacheResult(url, resultData);
    } catch (err) {
      console.error("Check URL error:", err);
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
      
      // Show estimated status on error (trust-based fallback)
      const domain = new URL(url).hostname;
      const fallbackResult: CheckResult = {
        status: "caution",
        domain,
        checks: {
          https: url.startsWith("https://"),
          domainAge: "estimated",
          domainAgeEstimated: true,
          publicThreatFeeds: "unknown",
        },
        error: "Some checks could not be completed",
      };
      setResult(fallbackResult);
      // Don't cache error results
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectCachedUrl = (url: string) => {
    handleCheck(url);
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
  };

  // Auto-check if site parameter is present
  useEffect(() => {
    if (initialUrl && !hasAutoChecked.current) {
      hasAutoChecked.current = true;
      let normalizedUrl = initialUrl.trim();
      if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
        normalizedUrl = `https://${normalizedUrl}`;
      }
      handleCheck(normalizedUrl);
    }
  }, [initialUrl]);

  return (
    <div className="space-y-8">
      <UrlCheckerForm onCheck={handleCheck} isLoading={isLoading} initialUrl={initialUrl} />
      
      {error && !result && (
        <div className="text-center p-4 bg-risk-bg rounded-lg border border-risk/30">
          <p className="text-risk">{error}</p>
        </div>
      )}
      
      {isLoading && <UrlCheckerSkeleton />}
      
      {result && !isLoading && (
        <>
          <UrlCheckerResult result={result} />
          <div className="text-center">
            <button
              onClick={handleReset}
              className="text-sm text-primary hover:underline"
            >
              Check another website
            </button>
          </div>
        </>
      )}

      <CacheList
        cachedUrls={cachedUrls}
        onClearEntry={clearCacheEntry}
        onClearAll={clearAllCache}
        onSelectUrl={handleSelectCachedUrl}
        getTimeRemaining={getTimeRemaining}
      />
    </div>
  );
}
